/**
 * `H` — the horizon line (§9.3, §8.2). Dragging it changes `depth.horizon_y`,
 * and the ghost silhouette next to the line shows `s(y)` live, which is the
 * only way a non-specialist can judge "does the fox look the right size".
 */
import { idle, none, withState, type ToolHandler, type ToolResult } from './types'

export const horizonTool: ToolHandler = {
  id: 'horizon',
  shortcut: 'h',
  icon: 'Minus',
  label: 'Horizon',
  hint: 'Drag the yellow line to where the ground meets the sky.',
  cursor: 'ns-resize',

  reduce(machine, ev, cfg): ToolResult | null {
    const st = machine.state

    if (ev.t === 'pointerdown' && ev.button === 0) {
      return withState('horizon', { t: 'horizon.dragging', y: ev.at[1] }, [{ k: 'beginGesture' }, { k: 'setHorizon', y: ev.at[1] }])
    }

    if (ev.t === 'pointermove') {
      if (st.t !== 'horizon.dragging') return none(machine)
      return withState('horizon', { t: 'horizon.dragging', y: ev.at[1] }, [{ k: 'setHorizon', y: ev.at[1] }])
    }

    if (ev.t === 'pointerup') {
      if (st.t !== 'horizon.dragging') return none(machine)
      return idle('horizon', [{ k: 'endGesture' }])
    }

    if (ev.t === 'key' && (ev.key === 'ArrowUp' || ev.key === 'ArrowDown')) {
      const current = cfg.horizonY
      if (current === undefined) return null
      const step = ev.shift ? 1 : 0.1
      const y = clamp(current + (ev.key === 'ArrowDown' ? step : -step), 0, 100)
      return idle('horizon', [{ k: 'beginGesture' }, { k: 'setHorizon', y }, { k: 'endGesture' }])
    }

    if (ev.t === 'cancel' || ev.t === 'pointercancel') {
      return st.t === 'horizon.dragging' ? idle('horizon', [{ k: 'cancelGesture' }]) : idle('horizon')
    }

    return null
  },
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)
